import * as moment from "moment";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { getElasticSearchURLS } from "../../../selectors/enviromentSelectors";
import PersesSearchAPI from "../../../services/persesSearchService";
import {
  DASHBOARD_RANGE_TYPES_DICTIONARY,
  DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES,
  QUERY_STATUS,
} from "../../../utils/constants/dashboard/dashboardModuleConstants";
import { LawsuitUpdatesCounterResponse } from "@customTypes/services/responses";

interface ParsedLawsuitUpdatesCounter {
  hasUpdates?: number;
  noUpdates?: number;
}

const parseLawsuitUpdatesCounterResponse = (
  response: LawsuitUpdatesCounterResponse
): ParsedLawsuitUpdatesCounter => ({
  hasUpdates: response.find((bucket) => bucket?.firstKey === "in")?.count,
  noUpdates: response.find((bucket) => bucket?.firstKey === "out")?.count,
});

const useLawsuitUpdatesCounterQuery = (
  types: string[],
  range: DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES
): [string, ParsedLawsuitUpdatesCounter] => {
  const [lawsuitUpdatesCount, setLawsuitUpdatesCount] = useState<ParsedLawsuitUpdatesCounter>();
  const [status, setStatus] = useState<(typeof QUERY_STATUS)[keyof typeof QUERY_STATUS]>(
    QUERY_STATUS.loading
  );
  const TODAY = moment().format("YYYY-MM-DD");
  const FROM_DATE =
    (
      DASHBOARD_RANGE_TYPES_DICTIONARY as Record<
        string,
        { label: string; range: { fromDate: string; toDate: string } }
      >
    )[range]?.range?.fromDate ?? TODAY;

  const PERSES_CODED_URLS = useSelector(getElasticSearchURLS);
  const perses = new PersesSearchAPI(PERSES_CODED_URLS);

  const { data, isSuccess, isLoading, isFetching } = useQuery(
    ["lateActivityCounter", { from: FROM_DATE, to: TODAY, types }],
    () =>
      perses.getLawsuitUpdatesCounterData(types, FROM_DATE, TODAY).then(({ buckets }) => buckets),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (isLoading) return;
    if (isFetching) {
      setStatus(QUERY_STATUS.refeching);
      return;
    }
    if (isSuccess) {
      const parsedData = parseLawsuitUpdatesCounterResponse(data);
      setLawsuitUpdatesCount(parsedData);
      setStatus(QUERY_STATUS.success);
    }
  }, [data, isSuccess, isLoading, isFetching]);

  return [status, lawsuitUpdatesCount];
};

export default useLawsuitUpdatesCounterQuery;
